import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import ThemeToggle from "./ThemeToggle";
import { useAuth, useIsAdmin } from 'shared/context/AuthContext';
import { useCustomPages } from 'shared/utils/pagesStorage';
import { Flex, Layout, Button, Input, theme } from 'antd';
const { Header } = Layout;


const HeaderLayout = ({ isCollapsed = false }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const isAdmin = useIsAdmin();
    const customPages = useCustomPages();
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);
    const notificationDropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Build user display data from auth context
    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const currentUser = {
        name: user ? `${user.name || ''} ${user.surname || ''}`.trim() || user.username : 'Пользователь',
        email: user?.email || '',
        role: user?.role || 'Пользователь',
        avatar: user?.avatarUrl || null,
        initials: getInitials(user ? `${user.name || ''} ${user.surname || ''}`.trim() || user.username : 'U')
    };

    // Mock notifications - in real app this would come from API
    const notifications = [
        {
            id: 1,
            type: 'security',
            title: 'Failed login attempts detected',
            message: '5 failed attempts from IP 192.168.1.100',
            time: '2 minutes ago',
            unread: true
        },
        {
            id: 2,
            type: 'user',
            title: 'New user registration',
            message: 'Sarah Johnson has registered for access',
            time: '15 minutes ago',
            unread: true
        },
        {
            id: 3,
            type: 'system',
            title: 'Session cleanup completed',
            message: 'Expired sessions have been cleared',
            time: '1 hour ago',
            unread: false
        }
    ];

    const unreadCount = notifications.filter(n => n.unread).length;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setIsProfileDropdownOpen(false);
            }
            if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
                setIsNotificationDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Закрывать раскрытое «Help & Support» при закрытии всего профильного меню
    useEffect(() => {
        if (!isProfileDropdownOpen) setIsHelpOpen(false);
    }, [isProfileDropdownOpen]);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error('Logout failed:', err);
        }
        navigate('/login');
    };

    const handleProfileSettings = () => {
        navigate('/profile');
        setIsProfileDropdownOpen(false);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'security':
                return 'Shield';
            case 'user':
                return 'User';
            case 'system':
                return 'Settings';
            default:
                return 'Bell';
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'security':
                return 'text-error';
            case 'user':
                return 'text-accent';
            case 'system':
                return 'text-secondary-500';
            default:
                return 'text-secondary-500';
        }
    };

    // Don't render header on authentication pages
    if (location.pathname === '/login' || location.pathname === '/register') {
        return null;
    }

    const { Search } = Input;
    const { token: { colorBgContainer, borderRadiusLG, colorBgLayout, colorBgBlur },
    } = theme.useToken();


    return (
        <Header className="pl-5 p-0 " style={{ background: colorBgBlur, borderRadius: 'none' }}>
            <Flex flex justify="space-between" align="flex-start"
                className={` h-full transition-all duration-300 ease-out
                    ${isCollapsed ? '' : ''}
                `}
            >
                {/* Search Bar - Desktop */}
                <Flex flex align="flex-start" justify="flex-start" className="hidden md:flex mt-4">
                    <Search
                        allowClear
                        className="w-fit"
                        placeholder="Search users, roles, sessions..."
                        name="full-search"
                    />
                </Flex>
                <Flex flex align="center" gap="small" justify="flex-end">
                    {/* Right Side Actions */}
                    {/* Search Button - Mobile */}
                    <button className="md:hidden  rounded-md hover:bg-secondary-50 transition-colors duration-150">
                        <Icon name="Search" size={20} className="text-secondary-600" />
                    </button>

                    {/* Theme toggle */}
                    <ThemeToggle />

                    {/* Notifications Dropdown */}
                    <div className="relative" ref={notificationDropdownRef}>
                        <button
                            onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
                            className="
                relative p-2 rounded-md hover:bg-secondary-50 transition-all duration-150 hover-lift
                focus:outline-none focus:ring-2 focus:ring-primary/20
              "
                        >
                            <Icon name="Bell" size={20} className="text-secondary-600" />
                            {unreadCount > 0 && (
                                <span className="
                  absolute -top-1 -right-1 w-5 h-5 bg-error text-white text-xs
                  rounded-full flex items-center justify-center font-medium
                  animate-pulse-status
                ">
                                    {unreadCount}
                                </span>
                            )}
                        </button>

                        {isNotificationDropdownOpen && (
                            <div className="
                absolute right-0 mt-2 w-80 bg-surface rounded-lg shadow-lg border border-secondary-200
                z-dropdown glass-morphism
              ">
                                <div className="p-4 border-b border-secondary-100">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-semibold text-text-primary">Notifications</h3>
                                        {unreadCount > 0 && (
                                            <span className="text-xs text-accent font-medium">
                                                {unreadCount} new
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="max-h-96 overflow-y-auto">
                                    {notifications.map(notification => (
                                        <div
                                            key={notification.id}
                                            className={`
                        p-4 border-b border-secondary-50 hover:bg-secondary-25 transition-colors duration-150
                        ${notification.unread ? 'bg-primary-50/30' : ''}
                      `}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className={`p-1.5 rounded-md bg-secondary-100 ${getNotificationColor(notification.type)}`}>
                                                    <Icon name={getNotificationIcon(notification.type)} size={14} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm text-text-primary truncate">
                                                        {notification.title}
                                                    </p>
                                                    <p className="text-xs text-text-secondary mt-1 line-clamp-2">
                                                        {notification.message}
                                                    </p>
                                                    <p className="text-xs text-secondary-400 mt-2">
                                                        {notification.time}
                                                    </p>
                                                </div>
                                                {notification.unread && (
                                                    <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="p-3 border-t border-secondary-100">
                                    <button
                                        className="w-full text-center text-sm text-primary hover:text-primary-700 font-medium transition-colors duration-150">
                                        View all notifications
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* User Profile Dropdown */}
                    <div className="relative" ref={profileDropdownRef}>
                        <button
                            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                            className="
                flex items-center gap-3 p-2 rounded-md hover:bg-secondary-50 
                transition-all duration-150 hover-lift focus:outline-none focus:ring-2 focus:ring-primary/20
              "
                        >
                            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-white">
                                    {currentUser.initials}
                                </span>
                            </div>
                            <div className="hidden sm:block text-left">
                                <p className="text-sm font-medium text-text-primary">
                                    {currentUser.name}
                                </p>
                                <p className="text-xs text-text-secondary">
                                    {currentUser.role}
                                </p>
                            </div>
                            <Icon name="ChevronDown" size={16} className="text-secondary-400" />
                        </button>

                        {isProfileDropdownOpen && (
                            <div className="
                absolute right-0 mt-2 w-64 bg-surface rounded-lg shadow-lg border border-secondary-200
                z-dropdown glass-morphism
              ">
                                <div className="p-4 border-b border-secondary-100">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                                            <span className="text-sm font-medium text-white">
                                                {currentUser.initials}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-text-primary">
                                                {currentUser.name}
                                            </p>
                                            <p className="text-sm text-text-secondary">
                                                {currentUser.email}
                                            </p>
                                            <p className="text-xs text-accent font-medium mt-1">
                                                {currentUser.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-2">
                                    <button
                                        onClick={handleProfileSettings}
                                        className="
                      w-full flex items-center gap-3 px-3 py-2 rounded-md
                      hover:bg-secondary-50 transition-colors duration-150 text-left
                    "
                                    >
                                        <Icon name="Settings" size={16} className="text-secondary-500" />
                                        <span className="text-sm text-text-primary">Profile Settings</span>
                                    </button>

                                    <button
                                        onClick={() => setIsHelpOpen((v) => !v)}
                                        className="
                      w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md
                      hover:bg-secondary-50 transition-colors duration-150 text-left
                    "
                                        aria-expanded={isHelpOpen}
                                    >
                                        <span className="flex items-center gap-3">
                                            <Icon name="HelpCircle" size={16} className="text-secondary-500" />
                                            <span className="text-sm text-text-primary">Help &amp; Support</span>
                                        </span>
                                        <Icon
                                            name={isHelpOpen ? 'ChevronUp' : 'ChevronDown'}
                                            size={14}
                                            className="text-secondary-400"
                                        />
                                    </button>

                                    {isHelpOpen && (
                                        <div className="pl-7 pr-1 pb-1 space-y-0.5">
                                            <button
                                                onClick={() => {
                                                    navigate('/privacy');
                                                    setIsProfileDropdownOpen(false);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-secondary-50 transition-colors duration-150 text-left"
                                            >
                                                <Icon name="ShieldCheck" size={14} className="text-secondary-500" />
                                                <span className="text-sm text-text-primary">Политика конфиденциальности</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    navigate('/terms');
                                                    setIsProfileDropdownOpen(false);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-secondary-50 transition-colors duration-150 text-left"
                                            >
                                                <Icon name="FileText" size={14} className="text-secondary-500" />
                                                <span className="text-sm text-text-primary">Условия использования</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    navigate('/support');
                                                    setIsProfileDropdownOpen(false);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-secondary-50 transition-colors duration-150 text-left"
                                            >
                                                <Icon name="LifeBuoy" size={14} className="text-secondary-500" />
                                                <span className="text-sm text-text-primary">Поддержка и FAQ</span>
                                            </button>

                                            {customPages.map((p) => (
                                                <button
                                                    key={p.slug}
                                                    onClick={() => {
                                                        navigate(`/pages/${p.slug}`);
                                                        setIsProfileDropdownOpen(false);
                                                    }}
                                                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-secondary-50 transition-colors duration-150 text-left"
                                                >
                                                    <Icon name="BookOpen" size={14} className="text-secondary-500" />
                                                    <span className="text-sm text-text-primary truncate">{p.title}</span>
                                                </button>
                                            ))}


                                        </div>
                                    )}

                                    <div className="border-t border-secondary-100 mt-2 pt-2">
                                        <button
                                            onClick={handleLogout}
                                            className="
                        w-full flex items-center gap-3 px-3 py-2 rounded-md
                        hover:bg-error-50 hover:text-error transition-colors duration-150 text-left
                      "
                                        >
                                            <Icon name="LogOut" size={16} className="text-secondary-500" />
                                            <span className="text-sm text-text-primary">Sign Out</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Flex>
            </Flex>
        </Header>
    );
};

export default HeaderLayout;